/**
 * The player's action-skill bar: 4 slots (keys 1-4) holding draggable skills. Edited in the Skills
 * page (K) and shown in the bottom-left skill bar HUD; pressing the slot's number activates it.
 * Slot assignments are client-local (persisted to localStorage) — purely a control-binding preference.
 */

export type SkillId = "attack" | "block";

export interface SkillDef {
  id: SkillId;
  name: string;
  /** Item-icon id used to draw the chip/slot art (via drawItemIcon). */
  icon: string;
}

/** The skills you can drag into the bar (for now: the two combat actions). */
export const SKILLS: SkillDef[] = [
  { id: "attack", name: "Attack", icon: "sword" },
  { id: "block", name: "Block", icon: "round_shield" },
];
export function skillDef(id: string | null): SkillDef | undefined {
  return id ? SKILLS.find((s) => s.id === id) : undefined;
}

/** Skills granted by carrying/equipping an item: the Iron Sword grants Attack, the Round Shield
 *  grants Block (so a sword alone can attack but can't block — you need the shield in your offhand). */
export const ITEM_SKILLS: Record<string, SkillId[]> = {
  sword: ["attack"],
  round_shield: ["block"],
};

/**
 * The skills currently AVAILABLE to the player — the deduped union of skills granted by their
 * equipped hands plus any learned skills. Duplicates (e.g. two weapons granting Attack) collapse to
 * one. Returned in SKILLS order. A skill is only usable / draggable when it's in this set.
 */
export function availableSkillIds(hand1: string, hand2: string, learned: SkillId[] = []): SkillId[] {
  const set = new Set<SkillId>();
  for (const id of [hand1, hand2]) for (const s of ITEM_SKILLS[id] ?? []) set.add(s);
  for (const s of learned) set.add(s);
  return SKILLS.filter((d) => set.has(d.id)).map((d) => d.id);
}

export const SKILL_SLOTS = 4;
type Slots = (SkillId | null)[];
type Listener = (slots: Slots) => void;

const STORAGE_KEY = "gl-skill-slots";
/** Default binding so combat works out of the box: 1 = Attack, 2 = Block. */
const DEFAULT_SLOTS: Slots = ["attack", "block", null, null];

function load(): Slots {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "");
    if (Array.isArray(raw)) {
      const out: Slots = [null, null, null, null];
      for (let i = 0; i < SKILL_SLOTS; i++) {
        const v = raw[i];
        out[i] = v === "attack" || v === "block" ? v : null;
      }
      return out;
    }
  } catch { /* fall through to defaults */ }
  return [...DEFAULT_SLOTS];
}

class SkillStore {
  private slots: Slots = load();
  private readonly listeners = new Set<Listener>();

  get(): Slots { return this.slots; }

  private commit(): void {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.slots)); } catch { /* ignore */ }
    for (const fn of this.listeners) fn(this.slots);
  }

  /** Put a skill in a slot. If it already sits in another slot, that one is cleared (no dupes). */
  setSlot(index: number, id: SkillId | null): void {
    if (index < 0 || index >= SKILL_SLOTS) return;
    if (id) for (let i = 0; i < SKILL_SLOTS; i++) if (this.slots[i] === id) this.slots[i] = null;
    this.slots[index] = id;
    this.commit();
  }

  /** Swap two slots (drag one slot onto another). */
  swap(a: number, b: number): void {
    if (a === b || a < 0 || b < 0 || a >= SKILL_SLOTS || b >= SKILL_SLOTS) return;
    [this.slots[a], this.slots[b]] = [this.slots[b], this.slots[a]];
    this.commit();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.slots);
    return () => this.listeners.delete(fn);
  }
}

export const skillStore = new SkillStore();
